import { getCookie, setCookie } from '../utils/cookies';
import { closeSheet, openSheet } from './ecko-sheets';

const ACCEPTED = 'accepted';
const DECLINED = 'declined';
let heartbeatStarted = false;
let heartbeatTimer = null;
let heartbeatStopped = false;

function getConfig() {
	return window.eckoAnalyticsV2 || {};
}

function getCommonParameters() {
	const config = getConfig();
	const params = config.commonParameters || {};
	const cleaned = {};

	Object.keys(params).forEach(key => {
		if (params[key] !== '') {
			cleaned[key] = params[key];
		}
	});

	return cleaned;
}

function ensureGtag() {
	window.dataLayer = window.dataLayer || [];
	window.gtag = window.gtag || function () {
		window.dataLayer.push(arguments);
	};
}

function loadGtag() {
	const config = getConfig();
	if (!config.measurementId || !config.scriptUrl) return;

	ensureGtag();

	if (!document.querySelector(`script[src="${config.scriptUrl}"]`)) {
		const script = document.createElement('script');
		script.async = true;
		script.src = config.scriptUrl;
		document.head.appendChild(script);
	}

	const commonParameters = getCommonParameters();
	window.gtag('js', new Date());
	window.gtag('set', commonParameters);
	window.gtag('config', config.measurementId, commonParameters);
}

function track(eventName, parameters = {}) {
	if (typeof window.gtag !== 'function') return;

	window.gtag('event', eventName, {
		...getCommonParameters(),
		...parameters
	});
}

function consentCookieName() {
	return getConfig().consentCookie || 'ecko_analytics_consent';
}

function hasAcceptedConsent() {
	return getCookie(consentCookieName()) === ACCEPTED;
}

function openCookieModal(modal) {
	if (!modal || modal.open) return;

	openSheet(modal);
}

function closeCookieModal(modal) {
	if (!modal) return;

	closeSheet(modal, { force: true });
}

function getRealtimeConfig() {
	return getConfig().realtime || {};
}

function generateVisitorToken() {
	if (window.crypto?.randomUUID) {
		return window.crypto.randomUUID();
	}

	if (window.crypto?.getRandomValues) {
		const bytes = new Uint8Array(16);
		window.crypto.getRandomValues(bytes);
		return Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('');
	}

	return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`;
}

function getVisitorToken() {
	const realtime = getRealtimeConfig();
	const key = realtime.tokenStorageKey || 'ecko_ga_v2_realtime_token';
	let token = '';

	try {
		token = window.sessionStorage.getItem(key) || '';
		if (!token) {
			token = generateVisitorToken();
			window.sessionStorage.setItem(key, token);
		}
	} catch (error) {
		token = generateVisitorToken();
	}

	return token;
}

function realtimeExpired() {
	const expiresAt = Number(getRealtimeConfig().expiresAt || 0);
	return !expiresAt || Date.now() >= expiresAt * 1000;
}

function stopRealtimeHeartbeat() {
	heartbeatStopped = true;

	if (heartbeatTimer) {
		window.clearTimeout(heartbeatTimer);
		heartbeatTimer = null;
	}
}

function heartbeatDelay() {
	const realtime = getRealtimeConfig();
	const interval = Number(realtime.heartbeatIntervalMs || 60000);
	const jitter = Number(realtime.jitterMs || 10000);
	const offset = jitter ? Math.round((Math.random() * jitter * 2) - jitter) : 0;

	return Math.max(10000, interval + offset);
}

function scheduleRealtimeHeartbeat(delay = heartbeatDelay()) {
	if (heartbeatStopped || realtimeExpired()) {
		stopRealtimeHeartbeat();
		return;
	}

	if (heartbeatTimer) {
		window.clearTimeout(heartbeatTimer);
	}

	heartbeatTimer = window.setTimeout(sendRealtimeHeartbeat, delay);
}

function sendRealtimeHeartbeat() {
	const realtime = getRealtimeConfig();

	if (heartbeatStopped || !realtime.enabled || !hasAcceptedConsent()) return;

	if (realtimeExpired()) {
		stopRealtimeHeartbeat();
		return;
	}

	if (document.visibilityState === 'hidden') {
		heartbeatTimer = null;
		return;
	}

	window.fetch(realtime.endpoint, {
		method: 'POST',
		credentials: 'same-origin',
		headers: {
			'Content-Type': 'application/json'
		},
		body: JSON.stringify({
			editionId: Number(realtime.editionId || 0),
			visitorToken: getVisitorToken()
		})
	}).catch(() => {
		// A missed heartbeat is tolerated by the 130-second active-reader window.
	}).finally(() => {
		scheduleRealtimeHeartbeat();
	});
}

function initRealtimeHeartbeat() {
	const realtime = getRealtimeConfig();

	if (heartbeatStarted || !realtime.enabled || !realtime.endpoint || !realtime.editionId || !hasAcceptedConsent()) {
		return;
	}

	heartbeatStarted = true;

	document.addEventListener('visibilitychange', () => {
		if (document.visibilityState === 'visible') {
			scheduleRealtimeHeartbeat(0);
		}
	});

	scheduleRealtimeHeartbeat(1000);
}

function initCookieConsent(onAccepted = () => {}) {
	const choice = getCookie(consentCookieName());

	if (choice === ACCEPTED) {
		loadGtag();
		onAccepted();
		return;
	}

	if (choice === DECLINED) {
		return;
	}

	const modal = document.getElementById('cookie-modal');
	if (!modal) return;


	modal.addEventListener('click', event => {
		const action = event.target.closest('[data-action]')?.getAttribute('data-action');

		if (action === 'cookies-accept') {
			setCookie(consentCookieName(), ACCEPTED, 365);
			closeCookieModal(modal);
			loadGtag();
			onAccepted();
		}

		if (action === 'cookies-decline') {
			setCookie(consentCookieName(), DECLINED, 30);
			closeCookieModal(modal);
		}
	});

	openCookieModal(modal);
}

function getTargetArticleFromHash(hash) {
	if (!hash || hash === '#') return null;

	try {
		return document.getElementById(decodeURIComponent(hash.slice(1)));
	} catch (error) {
		return null;
	}
}

function initCustomEventTracking() {
	window.addEventListener('ecko:yeargroupFilterChange', event => {
		const enabledSlugs = Array.isArray(event.detail?.enabledSlugs) ? event.detail.enabledSlugs : [];

		track('group_filter_change', {
			reason: event.detail?.reason || '',
			enabled_group_count: enabledSlugs.length
		});
	});

	window.addEventListener('ecko:eventAddToCalendarProvider', event => {
		track('calendar_event_add', {
			provider: event.detail?.provider || '',
			title: event.detail?.title || ''
		});
	});

	window.addEventListener('ecko:subscribeCalendarProvider', event => {
		track('calendar_subscribe', {
			provider: event.detail?.provider || ''
		});
	});

	window.addEventListener('ecko:storyDismiss', event => {
		track('story_dismiss', {
			story_id: event.detail?.storyId || ''
		});
	});

	window.addEventListener('ecko:storyUndoDismiss', event => {
		track('story_undo_dismiss', {
			story_id: event.detail?.storyId || ''
		});
	});

	window.addEventListener('ecko:storyShare', event => {
		track('story_share', {
			story_id: event.detail?.storyId || '',
			story_anchor: event.detail?.anchor || ''
		});
	});

	window.addEventListener('ecko:languageSwitch', event => {
		track('language_switch', {
			language: event.detail?.language || '',
			default_language: event.detail?.defaultLanguage || ''
		});
	});

	document.addEventListener('click', event => {
		const tocLink = event.target.closest('.table-of-contents .toc-list a[href^="#"]');
		if (tocLink) {
			const article = getTargetArticleFromHash(tocLink.getAttribute('href'));

			track('toc_jump', {
				target: tocLink.getAttribute('href') || '',
				story_id: article?.getAttribute('data-story-id') || ''
			});
			return;
		}

		const link = event.target.closest('a[href]');
		if (!link || link.closest('#subscribe-modal, #event-modal')) return;

		let url;
		try {
			url = new URL(link.href, window.location.href);
		} catch (error) {
			return;
		}

		if (!['http:', 'https:'].includes(url.protocol) || url.origin === window.location.origin) {
			return;
		}

		track('external_link_click', {
			link_url: url.toString(),
			link_host: url.hostname,
			link_text: link.textContent?.trim().slice(0, 120) || ''
		});
	});
}

export function initAnalyticsV2() {
	if (!getConfig().enabled) return;

	initCookieConsent(initRealtimeHeartbeat);
	initCustomEventTracking();
}